import { inject, Injectable } from '@angular/core';
import { AppService } from 'app/app.service';
import { ThemeEngine } from 'app/shared/components/theme';


export interface Step {
    min: number,
    max: number,
    colour: string,
    background?: string,
}

export interface Thresholds {
    steps: Step[];
}

@Injectable()
export class RatingService {

    private appService = inject(AppService);
    private themeEngine = new ThemeEngine();

    constructor() { 
        if (this.appService?.theme?.settings) {
            this.themeEngine.settingsToVariables(this.appService.theme.settings);
        }
    }

    private readonly DEFAULT_THRESHOLDS: Thresholds = {
        "steps": [
            {
                "min": -Infinity,
                "max": 0,
                "colour": "var(--color-danger-color)"
            },
            {
                "min": 0,
                "max": 60,
                "colour": "var(--color-danger-color)"
            },
            {
                "min": 61,
                "max": 80,
                "colour": "var(--color-warning-color)"
            },
            {
                "min": 81,
                "max": 100,
                "colour": "var(--color-success-color)"
            }
        ]
    }

    /**
     * Convert from a feature flag string to a Thresholds object.
     * 
     * Input string will be formatted as such:
     * 
     * > ```0,60:var(--color-danger-color)\n61,80:var(--color-warning-color)\n81,100:var(--color-success-color)```
     */
    getThresholdsFromString(thresholds: string): Thresholds {
        if (thresholds?.length > 0) {
            const steps = [];
            const originalSteps = thresholds.split('\n');
            for (const step of originalSteps) {
                const [range, colour] = step.split(':');
                const [min, max] = range.split(',');
                steps.push({
                    min: parseInt(min),
                    max: parseInt(max),
                    colour
                });
            }
            return {
                steps
            }
        } else {
            return this.DEFAULT_THRESHOLDS;
        }
    }

    getThresholdColour(value: number, thresholds: Thresholds = this.DEFAULT_THRESHOLDS, asHex?: boolean): string {
        const matchingThreshold = thresholds.steps.filter(step => value >= step.min && value <= (step.max + 1));
        const colour = matchingThreshold?.[0]?.colour;
        if (asHex && colour) {
            return this.computeColor(colour);
        }
        return colour ?? 'grey';
    }

    private computeColor(variable: string): string {
        return this.themeEngine.resolveFormula(variable);
    }

}
